import type { ApprovalData, SportsbookApproval } from '@/api/availability'
import type { GeoData } from '@/api/geo'

export type BlockReason = 'sportsbook_not_active' | null

export interface BetGateResult {
  allowed: boolean
  reason: BlockReason
}

const ALLOWED: BetGateResult = { allowed: true, reason: null }
const BLOCKED: BetGateResult = { allowed: false, reason: 'sportsbook_not_active' }

// Sportsbooks whose catalog is split into geo-targeted variants. Mirrors
// SPORTSBOOK_VARIANTS in api/affiliateLinks.ts. When gateBet is called with
// the canonical name, we try each variant in order — ALLOWED if any variant
// admits the visitor.
const SPORTSBOOK_VARIANTS: Record<string, string[]> = {
  Stake: ['Stake US', 'Stake Global'],
}

/**
 * Pick the variant of a geo-split sportsbook that admits the visitor, or
 * null when no variant matches. Used by the bet slip overlay to resolve the
 * canonical "Stake" placement to a concrete affiliate URL at click time.
 */
export function pickAllowedVariant(
  canonicalName: string,
  geo: GeoData | null | undefined,
  approvals: ApprovalData | null | undefined,
): string | null {
  const variants = SPORTSBOOK_VARIANTS[canonicalName]
  if (!variants) return null
  for (const v of variants) {
    if (gateBetSingle(v, geo, approvals).allowed) return v
  }
  return null
}

function findSportsbook(approvals: ApprovalData, name: string): SportsbookApproval | undefined {
  return approvals.sportsbooks.find(s => s.name === name)
}

/**
 * Gate a bet by SPORTSBOOK availability only (2026-07-02 policy change —
 * previously the sport's per-state approvals were checked too). Per-sport
 * approvals still drive the Distribution/Approvals surfaces; the widget's
 * action gate now asks a single question: can this visitor use this book
 * where they are? This is what makes Polymarket effectively global — it is
 * judged by its own availability row (42-state US allowlist + international
 * country denylist), not by each sport's approval map.
 */
export function gateBet(
  sportsbookName: string,
  geo: GeoData | null | undefined,
  approvals: ApprovalData | null | undefined,
): BetGateResult {
  const variants = SPORTSBOOK_VARIANTS[sportsbookName]
  if (variants) {
    // ALLOWED if any variant admits the visitor.
    for (const v of variants) {
      if (gateBetSingle(v, geo, approvals).allowed) return ALLOWED
    }
    return BLOCKED
  }
  return gateBetSingle(sportsbookName, geo, approvals)
}

function gateBetSingle(
  sportsbookName: string,
  geo: GeoData | null | undefined,
  approvals: ApprovalData | null | undefined,
): BetGateResult {
  if (!geo || !approvals) return ALLOWED

  const sportsbook = findSportsbook(approvals, sportsbookName)
  if (!sportsbook) return ALLOWED

  if (sportsbook.status?.startsWith('paused')) {
    return BLOCKED
  }

  if (!geo.countryCode) return ALLOWED

  if (geo.countryCode === 'US') {
    if (!geo.regionCode) return ALLOWED
    return sportsbook.active_states.includes(geo.regionCode) ? ALLOWED : BLOCKED
  }

  // Non-US: heterogeneous international arrays — sometimes country names
  // ("Argentina"), sometimes region names ("Ontario"). Match against either.
  //
  // Book international gate, additive across the allowed + blocked lists:
  //  - Denylist book (has a blocked list): active everywhere EXCEPT blocked.
  //    An Allowed list, if also present, narrows further (must be in it too).
  //    Empty Allowed + non-empty Blocked = pure denylist (e.g. Polymarket).
  //  - Allowlist book (no blocked list): must appear in the Allowed list;
  //    an empty Allowed list means not active internationally (unchanged).
  const userTokens = [geo.country, geo.region].filter(Boolean) as string[]
  const blocked = sportsbook.international_blocked ?? []
  const bookInt = blocked.length > 0
    ? !userTokens.some(t => blocked.includes(t))
      && (sportsbook.international.length === 0 || userTokens.some(t => sportsbook.international.includes(t)))
    : userTokens.some(t => sportsbook.international.includes(t))

  return bookInt ? ALLOWED : BLOCKED
}
